import React, { useContext, useEffect, useMemo } from "react";
import TableRow, { PrintRowKind } from "./table-row";
import { useChunkedRender } from "./use-chunked-render";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { TSubmittalGridRow, TMaterialGridRow } from "../grid-utils";
import {
  MATERIAL_PRINT_COLUMNS,
  PrintColumn,
  SUBMITTAL_PRINT_COLUMNS,
  filterPrintColumns,
  resolvePrintColumnLabel
} from "./print-columns";

type Props = {
  kind: PrintRowKind;
  rows: (TSubmittalGridRow | TMaterialGridRow)[];
  projectId: string;
  chunkSize: number;
  onComplete?: () => void;
  onProgress?: (visibleCount: number, totalCount: number) => void;
  rasterize?: boolean;
  visibleColumnIds?: string[];
};

function TableView({
  kind,
  rows,
  projectId,
  chunkSize,
  onComplete,
  onProgress,
  rasterize,
  visibleColumnIds
}: Props) {
  const { visible, isComplete } = useChunkedRender(rows, chunkSize, onComplete);

  useEffect(() => {
    onProgress?.(visible.length, rows.length);
  }, [visible.length, rows.length, onProgress]);

  const { columnHeaders }: TProjectContext = useContext(ProjectContext);
  const headerMap =
    kind === "submittal"
      ? columnHeaders?.submittalHeaderMap
      : columnHeaders?.materialHeaderMap;

  const columns = useMemo<PrintColumn<any>[]>(() => {
    const all: PrintColumn<any>[] =
      kind === "submittal" ? SUBMITTAL_PRINT_COLUMNS : MATERIAL_PRINT_COLUMNS;
    return filterPrintColumns(all, visibleColumnIds);
  }, [kind, visibleColumnIds]);

  return (
    // overflow-x-auto is deliberately dropped here. The wrapper's
    // overflow-x would make it a scroll container and trap our sticky
    // thead inside it — sticky only works against the nearest scrolling
    // ancestor, and we want the header to follow the page's vertical
    // scroll. The table uses explicit column widths + w-full, so horizontal
    // overflow is rare on desktop; on narrow viewports the body scrolls
    // horizontally instead.
    <div>
      <table className="w-full border-collapse text-[12px]">
        {/*
          Two behaviours on the same <thead>:
          - On screen: `sticky top-[56px]` keeps the column headers pinned
            just below the page-level sticky toolbar (~56px tall). z-10 so
            row content scrolls underneath, z-20 toolbar stays above.
          - On print: `print:table-header-group` makes browsers repeat this
            <thead> at the top of every paginated page. Without it only
            page 1 carries the column labels. `print:static` clears the
            sticky positioning so the print pipeline treats the <thead>
            as a normal header-group.
        */}
        <thead className="sticky top-[56px] z-10 print:static print:top-auto print:table-header-group">
          <tr>
            {columns.map((c) => (
              <th
                key={c.id}
                className="bg-white border border-solid border-ciq-gray-400 px-3 py-2 text-left align-bottom text-[13px] font-bold tracking-[0.08em] text-ciq-black-a87"
              >
                {resolvePrintColumnLabel(c, headerMap)}
              </th>
            ))}
          </tr>
        </thead>
        {/*
          Each TableRow renders its own <tbody class="break-inside-avoid">
          so the summary+detail pair stays together across page breaks.
          Tables allow multiple tbody elements, so no outer wrapper here.
        */}
        {visible.map((row) => (
          <TableRow
            key={row.id}
            kind={kind}
            row={row}
            projectId={projectId}
            rasterize={rasterize}
            columns={columns}
          />
        ))}
      </table>
      {/*
        Per-view progress text retired — page-level sticky bottom bar
        now shows unified render + rasterize progress.
      */}
      {!isComplete && <div className="h-0" aria-hidden />}
    </div>
  );
}

export default React.memo(TableView);
